/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prefer-destructuring */
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useReducer,
  useEffect,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { find } from 'lodash-es';
import { PNCounterContext } from '@mummssoftware/global-context/PNCounter';
import { ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2 } from '@mummssoftware/global-context/PNCounter/constants';
import {
  SET_CPP_NOTES,
  SET_SUBMITTED_ANSWERED_PATIENT_CPP,
  SET_RESOLVED_ANSWERED_PATIENT_CPP,
  CPPGlobalContext,
} from '@mummssoftware/global-context/CPPGlobalContext';
import {
  TaskGlobalContext,
  SET_PATIENT_ALL_CPP_TASKS,
} from '@mummssoftware/global-context/TaskGlobalContext';
import { canEditCPPGroup } from '@mummssoftware/utils/utils';
import { ANSWERED } from '@mummssoftware/utils/constants';
import { hasPlanTasks } from './utils';
import { isTaskSelected } from '../TaskPlanner/utils';
import { Context } from '../../context';
import { Other } from '../CPPEngine/constants';
import {
  Editing,
  Rendering,
  SET_CPP,
  SET_ERROR,
  SUBMIT_PLAN,
  SET_SUBMIT_PLAN_ACTIVE,
  SET_RESOLVE_DATE,
  DISCARD_EDIT,
  RESET_DISCARD_EDIT,
  SET_CPP_TASKS,
  SET_INITIAL_CPP_TASKS,
  SET_PREVIEW_MODE,
} from './constants';
import defaults from '../../translations/en.json';

type CPPContextProviderProps = {
  children: ReactNode;
};

type EntityNote = { count: string; entitykey: string; entitytype: string };

type CPPContext = {
  cppForm: mumms.CPPEntity | null;
  error: string | null;
  submitPlan: mumms.SubmitPayload;
  submitPlanActive: boolean;
  resolveDate: any;
  discardEdit: boolean;
  resetDiscardEdit: boolean;
  cppTasks: mumms.PatientCppTask[] | null;
  initialCppTasks: mumms.PatientCppTask[] | null;
  getPatientCppTasks: (
    cpp: mumms.patientCarePlanProblem,
    readonly?: boolean,
  ) => any;
  getDetailedPlan: (
    cpp: mumms.patientCarePlanProblem,
    readonly?: boolean,
  ) => any;
  getOtherTask: (e: string, type: mumms.CPPType) => any;
  otherTask: mumms.PatientCppTask[] | null;
  previewMode: boolean;
  canEditCPPRoleGroup: boolean;
  dispatch?: any;
};

type CPPContextState = {
  cppForm: mumms.CPPEntity | null;
  error: string | null;
  submitPlan: mumms.SubmitPayload;
  submitPlanActive: boolean;
  resolveDate: any;
  cppTasks: mumms.PatientCppTask[] | null;
  initialCppTasks: mumms.PatientCppTask[] | null;
  discardEdit: boolean;
  resetDiscardEdit: boolean;
  previewMode: boolean;
};
const initialState = {
  cppForm: null,
  error: null,
  submitPlan: null,
  submitPlanActive: false,
  discardEdit: false,
  resetDiscardEdit: false,
  resolveDate: null,
  cppTasks: null,
  initialCppTasks: null,
  previewMode: false,
};

const initCPPContext = {
  cppForm: null,
  error: null,
  submitPlan: null,
  submitPlanActive: false,
  discardEdit: false,
  resetDiscardEdit: false,
  resolveDate: null,
  cppTasks: null,
  initialCppTasks: null,
  previewMode: false,
};

export const CPPContextReducer = (
  state: CPPContextState,
  action: { type: string; payload: any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_CPP:
      newState.cppForm = action.payload;
      break;
    // this is used to display text inside CPP
    case SET_ERROR:
      newState.error = action.payload;
      break;
    // triggers the onSubmit function
    case SUBMIT_PLAN:
      newState.submitPlan = action.payload;
      break;
    // submit plan disabled until click on change happens
    case SET_SUBMIT_PLAN_ACTIVE:
      newState.submitPlanActive = action.payload;
      break;
    case SET_RESOLVE_DATE:
      newState.resolveDate = action.payload;
      break;
    case DISCARD_EDIT:
      newState.discardEdit = action.payload;
      break;
    case RESET_DISCARD_EDIT:
      newState.resetDiscardEdit = action.payload;
      break;
    // point of reference
    case SET_INITIAL_CPP_TASKS:
      newState.initialCppTasks = action.payload;
      break;
    case SET_CPP_TASKS:
      // Updating arrays without mutation
      {
        const payload = action.payload;
        // if null (reset it) or initial API response
        if (!payload || Array.isArray(payload)) {
          newState.cppTasks = payload;
          // if single task being update in SingleTaskPlanner
        } else if (newState.cppTasks && newState.cppTasks.length) {
          if (payload.editTaskMode) {
            // add the task
            delete payload.editTaskMode;
            const updatedTasks = [...newState.cppTasks, payload];
            newState.cppTasks = updatedTasks;
          } else {
            // update the task
            const updatedTasks = newState.cppTasks.map((el) =>
              el.id === payload.id ? payload : el,
            );
            newState.cppTasks = updatedTasks;
          }
        }
      }
      break;
    case SET_PREVIEW_MODE:
      newState.previewMode = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const CPPContext = createContext<CPPContext>(
  initCPPContext as CPPContext,
);
export const CPPContextProvider = ({ children }: CPPContextProviderProps) => {
  const [
    {
      cppForm,
      error,
      submitPlan,
      submitPlanActive,
      resolveDate,
      discardEdit,
      resetDiscardEdit,
      cppTasks,
      initialCppTasks,
      previewMode,
    },
    dispatch,
  ] = useReducer(CPPContextReducer, initialState);
  const intl = useIntl();

  // todo global context trigger

  const { countValues, refreshEntityKey } = useContext(PNCounterContext);
  const {
    cppNotesGlobal,
    answeredSubmittedCPPGlobal,
    answeredResolvedCPPGlobal,
    dispatchCPP,
  } = useContext(CPPGlobalContext);
  const {
    observation,
    patient,
    currentUser,
    agency,
    setPatientCppTasks,
    savedCPPtasks,
    smallWidget,
    setErrorInfo,
  } = useContext(Context);
  const { patientAllCPPTasks, dispatchTask } = useContext(TaskGlobalContext);
  const canEditCPPRoleGroup = canEditCPPGroup(currentUser);

  const pcppNotes =
    countValues &&
    patient &&
    countValues[patient.patientNumber][ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2];

  const keyObject =
    refreshEntityKey &&
    find(pcppNotes, {
      entitykey: refreshEntityKey,
    });

  // if same key then the useEffect does not catch it
  // scenario: adding notes one by one to the same care plan
  const keyID = keyObject && `${keyObject.entitykey}-${keyObject.count}`;

  // on Mount
  // when the widget load
  // if Global CPP Context not updated then API call
  useEffect(() => {
    // make API calls when needed on MOUNT => widget instantiation and PRINT
    // if not already in CPP Global Context
    if (
      // makeAPICall &&
      pcppNotes &&
      pcppNotes.length &&
      !Object.keys(cppNotesGlobal).length
    ) {
      getAllNotes();
    }
  }, []);

  // when a care plan note is added fetch it
  useEffect(() => {
    // @note trigger only 1 single API call by care plan tagging
    // instead of JSON.stringify(pcppNotes) and multiple API calls
    // only worth the overhead if many API calls involved because tradeoff of find with lodash?
    // make one API call when a note is tagged to PCPP
    if (refreshEntityKey && keyID) {
      getKeyNotes(refreshEntityKey);
    }
  }, [keyID]);

  const getAllNotes = async () => {
    const pnNotes = await observation.getWidgetPnNotes(
      patient?.patientNumber as string,
      agency as string,
      ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2,
    );
    // Global CPP Context
    dispatchCPP({
      type: SET_CPP_NOTES,
      payload: pnNotes,
    });
  };

  const getKeyNotes = async (key: string) => {
    const notes = await observation.getProgressNotesByEntity(
      patient.patientNumber,
      agency,
      ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2,
      key,
    );
    const pnNotes = !(notes instanceof Response) ? notes : null;

    // Global CPP Context
    if (pnNotes) {
      cppNotesGlobal[key] = pnNotes;
    }

    dispatchCPP({
      type: SET_CPP_NOTES,
      payload: cppNotesGlobal,
    });
  };

  useEffect(() => {
    // one time loading
    // endpoint call when expanding care plan problems
    if (!smallWidget && !answeredSubmittedCPPGlobal) {
      getSubmittedAnsweredPlans();
    }
  }, []);

  /**
   * on Load get the SUBMITTED PLANS ANSWERS
   * -> for PRINTING from CPP widget (in progress and resolved not included)
   * -> for when clicking on the care plan problem to see detailed view
   */
  const getSubmittedAnsweredPlans = async () => {
    const latest: mumms.LatestCPP = 'true';
    const mode: mumms.CPPMode = ANSWERED;
    await observation
      .getPatientCarePlanProblemsv2(
        patient?.patientNumber,
        latest,
        mode,
        agency,
      )
      .then(async (response: any) => {
        if (!(response instanceof Response)) {
          dispatchCPP({
            type: SET_SUBMITTED_ANSWERED_PATIENT_CPP,
            payload: response,
          });
        }
        // no error if failed as this is cached data
      })
      .catch((e: any) => console.error('e', e));
  };

  // make Patient CPP Tasks API call
  const getPatientCppTasksAPI = async (
    cpp: mumms.patientCarePlanProblem, // readonly  for repeated API calls
  ) => {
    // call endpoint patient CPP tasks
    const tasksResponse = await observation.getPatientCppTasksById(
      cpp.id,
      agency,
    );

    return tasksResponse;
  };

  // get Patient CPP Tasks
  const getPatientCppTasks = async (
    cpp: mumms.patientCarePlanProblem, // readonly  for repeated API calls
    readonly?: boolean,
  ) => {
    if (hasPlanTasks(cpp)) {
      // readonly for printing and display plan on accordion click
      if (readonly) {
        const planTasks = patientAllCPPTasks && patientAllCPPTasks[cpp.id];
        if (planTasks && planTasks.length) {
          // do nothing
        } else {
          // dispatch to global store for printing
          const patientTasks = await getPatientCppTasksAPI(cpp);
          if (!(patientTasks instanceof Response)) {
            const selectedTasks = patientTasks.filter(
              (task: mumms.PatientCppTask) => isTaskSelected(task),
            );
            const allTasks = {
              ...patientAllCPPTasks,
              [cpp.id]: selectedTasks,
            };
            dispatchTask({
              type: SET_PATIENT_ALL_CPP_TASKS,
              payload: allTasks,
            });
          }
        }
      } else {
        // call endpoint patient CPP tasks
        const patientTasks = await getPatientCppTasksAPI(cpp);

        if (!(patientTasks instanceof Response)) {
          const cppId = patientTasks.length
            ? patientTasks[0].careplanproblem_id
            : null;
          // get the Other Task
          if (cppId) {
            await getOtherTask(cppId, Editing);
          }
          dispatch({
            type: SET_CPP_TASKS,
            payload: patientTasks,
          });
          dispatch({
            type: SET_INITIAL_CPP_TASKS,
            payload: patientTasks,
          });
        }
      }
    }
  };

  /**
   * Get answered plans for in progress and resolved
   * Submitted plans answers are in Global Store
   */

  const getDetailedPlan = async (
    cpp: mumms.patientCarePlanProblem,
    // readonly  for repeated API calls
    readonly?: boolean,
  ) => {
    // reset the error
    dispatch({
      type: SET_ERROR,
      payload: null,
    });
    const answeredPlans = [
      ...(answeredSubmittedCPPGlobal || []),
      ...(answeredResolvedCPPGlobal || []),
    ];

    const answeredPlan: any = answeredPlans.length
      ? find(answeredPlans, (plan) => plan.id === cpp.id)
      : null;

    // get Patient CPP Tasks if needed
    await getPatientCppTasks(cpp, readonly);

    if (answeredPlan) {
      return answeredPlan;
    }
    const mode: mumms.CPPMode = ANSWERED;

    const detailedPlan = await observation
      .getPatientCarePlanProblemByIdv2(cpp.id, mode, agency)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          // if resolved plan dispacth to global store
          if (res.resolveddate) {
            // check if not null
            const storeResolvedPlans = answeredResolvedCPPGlobal || [];
            const resolvedPayload = [...storeResolvedPlans, res];
            dispatchCPP({
              type: SET_RESOLVED_ANSWERED_PATIENT_CPP,
              payload: resolvedPayload,
            });
          }
          return res;
          // eslint-disable-next-line no-else-return
        } else {
          const payloadText = intl.formatMessage(defaults.errorMsg, {
            status: `${res.status}`,
            cpp: `${cpp.careplanproblemname}`,
          });
          dispatch({
            type: SET_ERROR,
            payload: payloadText,
          });
          setErrorInfo({
            text: payloadText,
            severity: 'error',
          });
          return null;
        }
      })
      .catch(() => {
        dispatch({
          type: SET_ERROR,
          payload: intl.formatMessage(defaults.errorMsg, {
            status: '',
            cpp: `${cpp.careplanproblemname}`,
          }),
        });
        return null;
      });

    return detailedPlan;
  };

  const [otherTask, setOtherTask] = useState<mumms.PatientCppTask[] | null>(
    null,
  );

  const getOtherTask = async (cppId: string, type: mumms.CPPType) => {
    let tasksResponse: any;
    if (type === Rendering) {
      tasksResponse = await observation.getCppTasksById(cppId, agency, Other);
    } else {
      tasksResponse = await observation.getCppTasksById(cppId, agency, Other);
    }
    if (!(tasksResponse instanceof Response)) {
      setOtherTask(tasksResponse);
    }
  };

  useEffect(() => {
    if (savedCPPtasks) {
      dispatch({
        type: SET_INITIAL_CPP_TASKS,
        payload: null,
      });
    }
  }, [savedCPPtasks]);

  // discardEdit resets
  useEffect(() => {
    if (discardEdit) {
      dispatch({
        type: SET_CPP_TASKS,
        payload: null,
      });
      dispatch({
        type: SET_INITIAL_CPP_TASKS,
        payload: null,
      });
    }
  }, [discardEdit]);

  const [taskReady, setTaskReady] = useState<boolean>(false);

  /**
   * verify id of CPP and Tasks
   * diff strategy : in FormRenderer pass formData.is to saveCPP and check in function
   */
  const checkCppTaks = () => {
    if (cppTasks && cppTasks.length && cppForm) {
      const task = cppTasks[0];
      const taskCppId =
        task.patientcareplanproblem_id || task.careplanproblem_id;
      if (taskCppId === cppForm.id) {
        setPatientCppTasks(cppTasks);
      }
    }
    setTaskReady(false);
  };

  /**
   * on submitPlan, gather the CPP tasks if appropriate
   * setTimeOut to have time to gather all tasks
   */
  useEffect(() => {
    setTimeout(() => {
      const hasTasks = cppTasks && cppTasks.length && cppForm;
      if (hasTasks && submitPlan) {
        setTaskReady(true);
      }
    }, 500);
  }, [submitPlan]);

  /**
   * verify this is the correct CPP
   */
  useEffect(() => {
    if (taskReady) {
      checkCppTaks();
    }
  }, [taskReady]);

  const context = useMemo(
    () => ({
      cppForm,
      error,
      submitPlan,
      submitPlanActive,
      resolveDate,
      discardEdit,
      resetDiscardEdit,
      cppTasks,
      initialCppTasks,
      previewMode,
      getPatientCppTasks,
      getDetailedPlan,
      getOtherTask,
      otherTask,
      canEditCPPRoleGroup,
      dispatch,
    }),
    [
      cppForm,
      error,
      submitPlan,
      submitPlanActive,
      resolveDate,
      discardEdit,
      resetDiscardEdit,
      cppTasks,
      initialCppTasks,
      previewMode,
      getPatientCppTasks,
      getDetailedPlan,
      getOtherTask,
      otherTask,
      canEditCPPRoleGroup,
      dispatch,
    ],
  );
  return <CPPContext.Provider value={context}>{children}</CPPContext.Provider>;
};

export default CPPContextProvider;
